/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, HostListener, OnInit, signal} from '@angular/core';
import {EsciComponent} from '../esci.component';
import {UrlRequestActivityReport} from '../../model/urlRequestActivityReport';
import {UrlRequestLog} from '../../model/urlRequestLog';
import {PagingResult} from '../../model/pagingResult';
import {ColDef} from 'ag-grid-community';

interface DataPoint {
    x: number;
    y: number;
    timeRange: string;
    count: number;
    logEntries: Array<{ url: string; responseCode: number; count: number }>;
    key: string; // Unique key for this point: timeRange|count
}

interface LineSegment {
    startPoint: DataPoint;
    endPoint: DataPoint;
    urls: Array<{ url: string; responseCode: number }>;
    color: string;
    key: string; // Unique key for this line segment
    points: { x: number; y: number }[]; // For rendering the polyline
}

@Component({
    selector: 'app-url-request-activity-report',
    templateUrl: './url-request-activity-report.component.html',
    styleUrls: ['./url-request-activity-report.component.scss'],
    standalone: false
})
export class UrlRequestActivityReportComponent extends EsciComponent implements OnInit {
    private readonly loadingSignal = signal(false);
    reportType: string = 'Grid'; // Default to 'Grid'
    timeGrouping: string = 'day'; // Used for chart views (day/hour/minute)
    reportData: UrlRequestActivityReport[] = [];
    dataPoints: DataPoint[] = []; // Stage 1: All unique points
    lineSegments: LineSegment[] = []; // Stage 2: All line segments connecting points
    permissionUrl = '/esciUI/esciITAdminPermission';

    // Grid data
    gridData: UrlRequestLog[] = [];
    gridColumnDefs: ColDef[] = [
        {field: 'id', headerName: 'ID', maxWidth: 100, sortable: true, filter: 'agNumberColumnFilter', floatingFilter: true},
        {
            field: 'logTimestamp',
            headerName: 'Timestamp',
            minWidth: 180,
            sortable: true,
            filter: 'agDateColumnFilter',
            floatingFilter: true,
            filterParams: {
                defaultOption: 'equals',
                comparator: (filterLocalDateAtMidnight: Date, cellValue: string) => {
                    if (!cellValue) return -1;
                    const cellDate = new Date(cellValue);

                    // Extract date-only portions (year, month, day) ignoring time
                    const filterYear = filterLocalDateAtMidnight.getFullYear();
                    const filterMonth = filterLocalDateAtMidnight.getMonth();
                    const filterDay = filterLocalDateAtMidnight.getDate();

                    const cellYear = cellDate.getFullYear();
                    const cellMonth = cellDate.getMonth();
                    const cellDay = cellDate.getDate();

                    // Compare only date portions
                    if (cellYear < filterYear ||
                        (cellYear === filterYear && cellMonth < filterMonth) ||
                        (cellYear === filterYear && cellMonth === filterMonth && cellDay < filterDay)) {
                        return -1;
                    } else if (cellYear > filterYear ||
                               (cellYear === filterYear && cellMonth > filterMonth) ||
                               (cellYear === filterYear && cellMonth === filterMonth && cellDay > filterDay)) {
                        return 1;
                    }
                    return 0;
                }
            },
            valueFormatter: (params) => {
                if (params.value) {
                    const date = new Date(params.value);
                    return date.toLocaleString();
                }
                return '';
            }
        },
        {field: 'url', headerName: 'URL', minWidth: 300, sortable: true, filter: 'agTextColumnFilter', floatingFilter: true, flex: 1, filterParams: {defaultOption: 'contains'}},
        {field: 'gmin', headerName: 'GMIN', minWidth: 120, sortable: true, filter: 'agTextColumnFilter', floatingFilter: true, filterParams: {defaultOption: 'startsWith'}},
        {
            field: 'responseCode',
            headerName: 'Response Code',
            maxWidth: 130,
            sortable: true,
            filter: 'agTextColumnFilter',
            floatingFilter: true,
            filterParams: {defaultOption: 'startsWith'},
            comparator: (valueA: number, valueB: number) => {
                if (valueA == null && valueB == null) return 0;
                if (valueA == null) return -1;
                if (valueB == null) return 1;
                return valueA - valueB;
            },
            cellStyle: (params) => {
                if (params.value >= 400) {
                    return {color: '#DC143C', fontWeight: 'bold'};
                }
                return {color: '#0066CC'};
            }
        },
        {
            field: 'responseTimeMs',
            headerName: 'Response Time (ms)',
            maxWidth: 160,
            sortable: true,
            filter: 'agTextColumnFilter',
            floatingFilter: true,
            filterParams: {defaultOption: 'startsWith'},
            comparator: (valueA: number, valueB: number) => {
                if (valueA == null && valueB == null) return 0;
                if (valueA == null) return -1;
                if (valueB == null) return 1;
                return valueA - valueB;
            }
        },
        {
            field: 'responseSize',
            headerName: 'Response Size',
            maxWidth: 130,
            sortable: true,
            filter: 'agTextColumnFilter',
            floatingFilter: true,
            filterParams: {defaultOption: 'startsWith'},
            comparator: (valueA: number, valueB: number) => {
                if (valueA == null && valueB == null) return 0;
                if (valueA == null) return -1;
                if (valueB == null) return 1;
                return valueA - valueB;
            }
        },
        {
            field: 'headerSize',
            headerName: 'Header Size',
            maxWidth: 130,
            sortable: true,
            filter: 'agTextColumnFilter',
            floatingFilter: true,
            filterParams: {defaultOption: 'startsWith'},
            comparator: (valueA: number, valueB: number) => {
                if (valueA == null && valueB == null) return 0;
                if (valueA == null) return -1;
                if (valueB == null) return 1;
                return valueA - valueB;
            }
        }
    ];
    defaultColDef: ColDef = {
        resizable: true,
        sortable: true,
        filter: true,
        floatingFilter: true
    };

    // SVG chart properties
    width = 1200;
    height = 600;
    padding = 50;
    rightLabelWidth = 250; // Space for labels on the right
    connectionColumnWidth = 50; // Width of the connection column
    chartWidth = this.width - 2 * this.padding - this.rightLabelWidth - this.connectionColumnWidth;
    chartHeight = this.height - 2 * this.padding;

    // Time ranges for X-axis
    timeRanges: string[] = [];
    allTimeRanges: string[] = []; // All time ranges from min to max (including gaps)
    minTime: Date | null = null;
    maxTime: Date | null = null;
    maxCount = 0;

    // Tooltip
    tooltip = {visible: false, x: 0, y: 0, text: '', above: false};
    hoveredLineKeys: Set<string> = new Set(); // Track which segments/points are hovered (can be multiple for overlapping)
    hoveredUrls: Set<string> = new Set(); // Track which URLs should remain opaque (only those in the hovered segment/point)

    // Remove old LineData interface - no longer needed

    ngOnInit(): void {
        this.permission.set(false);
        this.esciService.checkPermission(this);
        this.loadReportData();
    }

    get loading(): boolean {
        return this.loadingSignal();
    }

    set loading(value: boolean) {
        this.loadingSignal.set(value);
    }

    onReportTypeChange(): void {
        this.loadReportData();
    }

    loadReportData(): void {
        this.loading = true;

        if (this.reportType === 'Grid') {
            // Load grid data
            this.esciService.getUrlRequestLogs(this, 0, 1000, () => {
                this.loading = false;
            }).subscribe({
                next: (pagingResult: PagingResult<UrlRequestLog>) => {
                    this.gridData = pagingResult.results || [];
                },
                error: (err: any) => {
                    console.error('Error loading grid data:', err);
                    this.loading = false;
                }
            });
        } else {
            // Load chart data - extract timeGrouping from reportType
            if (this.reportType === 'Group by Day') {
                this.timeGrouping = 'day';
            } else if (this.reportType === 'Group by Hour') {
                this.timeGrouping = 'hour';
            } else if (this.reportType === 'Group by Minute') {
                this.timeGrouping = 'minute';
            }

            this.esciService.getUrlRequestActivityReport(this, this.timeGrouping, () => {
                this.loading = false;
            }).subscribe({
                next: (data: UrlRequestActivityReport[]) => {
                    this.reportData = data;
                    this.processData();
                },
                error: (err: any) => {
                    console.error('Error loading report data:', err);
                    this.loading = false;
                }
            });
        }
    }

    processData(): void {
        // Extract unique time ranges and parse them to dates
        const timeRangeSet = new Set<string>();
        this.reportData.forEach(item => {
            timeRangeSet.add(item.timeRange);
        });
        this.timeRanges = Array.from(timeRangeSet).sort((a, b) => a.localeCompare(b));

        // Parse all time ranges to Date objects and find min/max
        const timeDates: Date[] = [];
        this.timeRanges.forEach(timeRange => {
            const date = this.parseTimeRange(timeRange);
            if (date) {
                timeDates.push(date);
            }
        });

        if (timeDates.length === 0) {
            this.minTime = null;
            this.maxTime = null;
            this.allTimeRanges = [];
            this.dataPoints = [];
            this.lineSegments = [];
            return;
        }

        this.minTime = new Date(Math.min(...timeDates.map(d => d.getTime())));
        this.maxTime = new Date(Math.max(...timeDates.map(d => d.getTime())));

        // Generate all time ranges from min to max (including gaps)
        this.allTimeRanges = this.generateAllTimeRanges(this.minTime, this.maxTime);

        // Find max count for Y-axis scaling
        this.maxCount = Math.max(...this.reportData.map(item => item.count), 0);
        if (this.maxCount === 0) {
            this.maxCount = 1; // Avoid division by zero
        }

        // For logarithmic scale, ensure maxCount is at least 1 (log(0) is undefined)
        if (this.maxCount < 1) {
            this.maxCount = 1;
        }

        // STAGE 1: Create DataPoint objects for every unique (timeRange, count) combination
        const pointMap = new Map<string, DataPoint>(); // Key: timeRange|count

        this.reportData.forEach(item => {
            // Only create points for positive counts
            if (item.count > 0) {
                const pointKey = `${item.timeRange}|${item.count}`;

                if (!pointMap.has(pointKey)) {
                    const date = this.parseTimeRange(item.timeRange);
                    if (date && this.minTime && this.maxTime) {
                        // Calculate X position based on actual time value
                        const timeDiff = this.maxTime.getTime() - this.minTime.getTime();
                        const positionFromMin = date.getTime() - this.minTime.getTime();
                        const x = this.padding + (positionFromMin / (timeDiff || 1)) * this.chartWidth;
                        // Use logarithmic scale for Y position (count > 0)
                        // Use log10(count + 1) to separate count 0 and count 1 visually
                        const y = item.count > 0
                            ? this.height - this.padding - (Math.log10(item.count + 1) / Math.log10(this.maxCount + 1)) * this.chartHeight
                            : this.height - this.padding; // Zero counts at bottom

                        pointMap.set(pointKey, {
                            x: x,
                            y: y,
                            timeRange: item.timeRange,
                            count: item.count,
                            logEntries: [],
                            key: pointKey
                        });
                    }
                }

                // Add this log entry to the point
                const point = pointMap.get(pointKey);
                if (point) {
                    point.logEntries.push({
                        url: item.url,
                        responseCode: item.responseCode,
                        count: item.count
                    });
                }
            }
        });

        this.dataPoints = Array.from(pointMap.values());

        // STAGE 2: Create LineSegment objects connecting points
        // Group data by URL + responseCode to track paths
        const urlPaths = new Map<string, Map<string, number>>(); // urlKey -> timeRange -> count

        this.reportData.forEach(item => {
            const urlKey = `${item.url}|${item.responseCode}`;
            if (!urlPaths.has(urlKey)) {
                urlPaths.set(urlKey, new Map());
            }
            urlPaths.get(urlKey)!.set(item.timeRange, item.count);
        });

        // For each URL path, ensure we have points for all time ranges (including 0 counts)
        urlPaths.forEach((timeRangeMap, urlKey) => {
            this.allTimeRanges.forEach(timeRange => {
                if (!timeRangeMap.has(timeRange)) {
                    timeRangeMap.set(timeRange, 0); // Add zero count for missing time ranges
                }
            });
        });

        // Create line segments for each URL path
        const segmentMap = new Map<string, LineSegment>(); // Key: startPointKey|endPointKey

        urlPaths.forEach((timeRangeMap, urlKey) => {
            // Convert to array and sort by timeRange
            const path: Array<{ timeRange: string; count: number }> = [];
            timeRangeMap.forEach((count, timeRange) => {
                path.push({timeRange, count});
            });

            path.sort((a, b) => {
                const dateA = this.parseTimeRange(a.timeRange);
                const dateB = this.parseTimeRange(b.timeRange);
                if (!dateA || !dateB) {
                    return 0;
                }
                return dateA.getTime() - dateB.getTime();
            });

            // Create segments between consecutive points (including zero-count points)
            for (let i = 0; i < path.length - 1; i++) {
                const start = path[i];
                const end = path[i + 1];

                // Get or create points for start and end (including zero-count points)
                const startPointKey = `${start.timeRange}|${start.count}`;
                const endPointKey = `${end.timeRange}|${end.count}`;

                // Find or create start point
                let startPoint = this.dataPoints.find(p => p.key === startPointKey);
                if (!startPoint && start.count === 0) {
                    // Create zero-count point if it doesn't exist
                    const date = this.parseTimeRange(start.timeRange);
                    if (date && this.minTime && this.maxTime) {
                        const timeDiff = this.maxTime.getTime() - this.minTime.getTime();
                        const positionFromMin = date.getTime() - this.minTime.getTime();
                        const x = this.padding + (positionFromMin / (timeDiff || 1)) * this.chartWidth;
                        const y = this.height - this.padding; // Count 0 is at the bottom

                        startPoint = {
                            x: x,
                            y: y,
                            timeRange: start.timeRange,
                            count: 0,
                            logEntries: [],
                            key: startPointKey
                        };
                        this.dataPoints.push(startPoint);
                    }
                }

                // Find or create end point
                let endPoint = this.dataPoints.find(p => p.key === endPointKey);
                if (!endPoint && end.count === 0) {
                    // Create zero-count point if it doesn't exist
                    const date = this.parseTimeRange(end.timeRange);
                    if (date && this.minTime && this.maxTime) {
                        const timeDiff = this.maxTime.getTime() - this.minTime.getTime();
                        const positionFromMin = date.getTime() - this.minTime.getTime();
                        const x = this.padding + (positionFromMin / (timeDiff || 1)) * this.chartWidth;
                        const y = this.height - this.padding; // Count 0 is at the bottom

                        endPoint = {
                            x: x,
                            y: y,
                            timeRange: end.timeRange,
                            count: 0,
                            logEntries: [],
                            key: endPointKey
                        };
                        this.dataPoints.push(endPoint);
                    }
                }

                if (startPoint && endPoint) {
                    const segmentKey = `${startPointKey}|${endPointKey}`;

                    if (!segmentMap.has(segmentKey)) {
                        const [url, responseCodeStr] = urlKey.split('|');
                        const responseCode = parseInt(responseCodeStr, 10);
                        const color = this.getColorForResponseCode(responseCode);

                        segmentMap.set(segmentKey, {
                            startPoint: startPoint,
                            endPoint: endPoint,
                            urls: [],
                            color: color,
                            key: segmentKey,
                            points: [{x: startPoint.x, y: startPoint.y}, {x: endPoint.x, y: endPoint.y}]
                        });
                    }

                    // Add this URL to the segment
                    const segment = segmentMap.get(segmentKey)!;
                    const [url, responseCodeStr] = urlKey.split('|');
                    const responseCode = parseInt(responseCodeStr, 10);

                    // Check if this URL is already in the segment
                    if (!segment.urls.find(u => u.url === url && u.responseCode === responseCode)) {
                        segment.urls.push({url: url, responseCode: responseCode});
                    }
                }
            }
        });

        this.lineSegments = Array.from(segmentMap.values());
    }

    parseTimeRange(timeRange: string): Date | null {
        if (this.timeGrouping === 'day') {
            // Format: "YYYY-MM-DD"
            // Use UTC to avoid timezone issues
            const parts = timeRange.split('-');
            if (parts.length !== 3) {
                return null;
            }
            const year = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
            const day = parseInt(parts[2], 10);
            const date = new Date(Date.UTC(year, month, day, 0, 0, 0));
            return isNaN(date.getTime()) ? null : date;
        } else {
            // Format: "YYYY-MM-DD HH24:MI:SS"
            const dateTimeParts = timeRange.split(' ');
            if (dateTimeParts.length !== 2) {
                return null;
            }
            const dateParts = dateTimeParts[0].split('-');
            const timeParts = dateTimeParts[1].split(':');
            if (dateParts.length !== 3 || timeParts.length !== 3) {
                return null;
            }
            const year = parseInt(dateParts[0], 10);
            const month = parseInt(dateParts[1], 10) - 1; // Month is 0-indexed
            const day = parseInt(dateParts[2], 10);
            const hour = parseInt(timeParts[0], 10);
            const minute = parseInt(timeParts[1], 10);
            const second = parseInt(timeParts[2], 10);
            const date = new Date(Date.UTC(year, month, day, hour, minute, second));
            return isNaN(date.getTime()) ? null : date;
        }
    }

    generateAllTimeRanges(minDate: Date, maxDate: Date): string[] {
        const ranges: string[] = [];
        const current = new Date(minDate);

        while (current <= maxDate) {
            const year = current.getUTCFullYear();
            const month = String(current.getUTCMonth() + 1).padStart(2, '0');
            const day = String(current.getUTCDate()).padStart(2, '0');

            if (this.timeGrouping === 'day') {
                ranges.push(`${year}-${month}-${day}`);
                current.setUTCDate(current.getUTCDate() + 1);
            } else if (this.timeGrouping === 'hour') {
                const hour = String(current.getUTCHours()).padStart(2, '0');
                ranges.push(`${year}-${month}-${day} ${hour}:00:00`);
                current.setUTCHours(current.getUTCHours() + 1);
            } else if (this.timeGrouping === 'minute') {
                const hour = String(current.getUTCHours()).padStart(2, '0');
                const minute = String(current.getUTCMinutes()).padStart(2, '0');
                ranges.push(`${year}-${month}-${day} ${hour}:${minute}:00`);
                current.setUTCMinutes(current.getUTCMinutes() + 1);
            }
        }

        return ranges;
    }

    getColorForResponseCode(responseCode: number): string {
        if (responseCode >= 400) {
            return '#DC143C'; // Red for errors
        }
        return '#0066CC'; // Blue for success
    }

    getOpacity(pointOrSegment: LineSegment | DataPoint): number {
        // If no points or segments are hovered, show all at full opacity
        if (this.hoveredLineKeys.size === 0) {
            return 1.0;
        }
        // If this point/segment is hovered, show at full opacity
        if (this.hoveredLineKeys.has(pointOrSegment.key)) {
            return 1.0;
        }
        // Otherwise, show at 20% opacity
        return 0.2;
    }

    getLabelOpacity(urlInfo: { url: string; responseCode: number; key: string }): number {
        // If no segments are hovered, show all labels at full opacity
        if (this.hoveredLineKeys.size === 0 || this.hoveredUrls.size === 0) {
            return 1.0;
        }

        // Only show labels for URLs that are in the hovered segment/point
        const urlKey = `${urlInfo.url}|${urlInfo.responseCode}`;
        if (this.hoveredUrls.has(urlKey)) {
            return 1.0;
        }

        // Otherwise, show at 20% opacity
        return 0.2;
    }

    getAllUniqueUrls(): Array<{ url: string; responseCode: number; color: string; key: string }> {
        const urlMap = new Map<string, { url: string; responseCode: number; color: string; key: string }>();

        this.lineSegments.forEach(segment => {
            segment.urls.forEach(urlInfo => {
                const key = `${urlInfo.url}|${urlInfo.responseCode}`;
                if (!urlMap.has(key)) {
                    urlMap.set(key, {
                        url: urlInfo.url,
                        responseCode: urlInfo.responseCode,
                        color: this.getColorForResponseCode(urlInfo.responseCode),
                        key: key
                    });
                }
            });
        });

        // Sort alphabetically by URL, then by response code
        return Array.from(urlMap.values()).sort((a, b) => {
            const urlCompare = a.url.localeCompare(b.url);
            if (urlCompare !== 0) {
                return urlCompare;
            }
            return a.responseCode - b.responseCode;
        });
    }

    onLabelMouseEnter(urlInfo: { url: string; responseCode: number; key: string }): void {
        this.hoveredLineKeys.clear();
        this.hoveredUrls.clear();

        // Only this URL should remain opaque
        this.hoveredUrls.add(`${urlInfo.url}|${urlInfo.responseCode}`);

        // Find all segments that contain this URL
        this.lineSegments.forEach(segment => {
            if (segment.urls.some(u => u.url === urlInfo.url && u.responseCode === urlInfo.responseCode)) {
                this.hoveredLineKeys.add(segment.key);
                // Also highlight the points
                this.hoveredLineKeys.add(segment.startPoint.key);
                this.hoveredLineKeys.add(segment.endPoint.key);
            }
        });
    }

    onLabelMouseLeave(): void {
        // Only clear if we're not currently showing a tooltip (i.e., not hovering over a graph line/point)
        if (!this.tooltip.visible) {
            this.hoveredLineKeys.clear();
        }
    }

    getXAxisLabelX(timeRange: string): number {
        if (!this.minTime || !this.maxTime) {
            return 0;
        }
        const date = this.parseTimeRange(timeRange);
        if (!date) {
            return 0;
        }

        const timeDiff = this.maxTime.getTime() - this.minTime.getTime();
        const positionFromMin = date.getTime() - this.minTime.getTime();
        return this.padding + (positionFromMin / (timeDiff || 1)) * this.chartWidth;
    }

    getRightLabelX(): number {
        return this.padding + this.chartWidth + this.connectionColumnWidth + 10;
    }

    getRightLabelY(index: number, total: number): number {
        // Distribute labels evenly along the right edge
        const labelSpacing = this.chartHeight / (total + 1);
        return this.padding + labelSpacing * (index + 1);
    }

    getLabelText(urlInfo: { url: string; responseCode: number }): string {
        return `${urlInfo.url} (${urlInfo.responseCode})`;
    }

    shouldShowXAxisLabel(timeRange: string): boolean {
        // Only show labels for time ranges that have data, or every Nth label to avoid crowding
        if (this.timeRanges.includes(timeRange)) {
            return true;
        }
        // For very long ranges, show every Nth label
        if (this.allTimeRanges.length > 20) {
            const index = this.allTimeRanges.indexOf(timeRange);
            return index % Math.ceil(this.allTimeRanges.length / 10) === 0;
        }
        return false;
    }

    getYAxisLabelY(value: number): number {
        // Use logarithmic scale for Y-axis label positioning
        if (value <= 0) {
            return this.height - this.padding; // Zero at bottom
        }
        // Use log10(value + 1) to match the data point positioning
        const logRatio = Math.log10(value + 1) / Math.log10(this.maxCount + 1);
        return this.height - this.padding - (logRatio * this.chartHeight);
    }

    getYAxisValues(): number[] {
        // Generate 6-8 evenly spaced values on a logarithmic scale
        const values: number[] = [];

        // Handle edge case: if maxCount is less than 1, just return [1]
        if (this.maxCount < 1) {
            return [1];
        }

        // Target 7 grid lines (6-8 range)
        const numLines = 7;

        // Calculate the logarithmic range
        const minLog = Math.log10(1); // log10(1) = 0
        const maxLog = Math.log10(this.maxCount + 1); // Use maxCount + 1 to match data positioning

        // Generate evenly spaced values in logarithmic space
        for (let i = 0; i < numLines; i++) {
            // Calculate log value at this step
            const logValue = minLog + (maxLog - minLog) * (i / (numLines - 1));
            // Convert back to linear value and subtract 1 (since we use count + 1 in positioning)
            let value = Math.pow(10, logValue) - 1;

            // Round to a nice number for display
            if (value < 1) {
                value = Math.round(value * 10) / 10; // Round to 1 decimal for values < 1
            } else if (value < 10) {
                value = Math.round(value); // Round to integer for values 1-9
            } else if (value < 100) {
                value = Math.round(value / 5) * 5; // Round to nearest 5 for values 10-99
            } else if (value < 1000) {
                value = Math.round(value / 10) * 10; // Round to nearest 10 for values 100-999
            } else {
                value = Math.round(value / 100) * 100; // Round to nearest 100 for larger values
            }

            // Ensure value is at least 1 and doesn't exceed maxCount
            value = Math.max(1, Math.min(value, this.maxCount));

            // Avoid duplicates
            if (values.length === 0 || values[values.length - 1] < value) {
                values.push(value);
            }
        }

        // Always include maxCount if it's not already there
        if (values.length === 0 || values[values.length - 1] < this.maxCount) {
            values.push(this.maxCount);
        }

        return values;
    }

    @HostListener('mousemove', ['$event'])
    onMouseMove(event: MouseEvent): void {
        if (this.tooltip.visible) {
            this.updateTooltipPosition(event);
        }
    }

    updateTooltipPosition(event: MouseEvent): void {
        // Get the chart container element
        const chartContainer = (event.target as HTMLElement).closest('.chart-container');
        if (!chartContainer) {
            // Fallback to page coordinates if container not found
            this.tooltip.x = event.pageX + 10;
            this.tooltip.y = event.pageY + 10;
            this.tooltip.above = false;
            return;
        }

        const containerRect = chartContainer.getBoundingClientRect();
        const mouseX = event.clientX - containerRect.left;
        const mouseY = event.clientY - containerRect.top;
        const containerHeight = containerRect.height;

        // Check if mouse is in lower third of the container
        const isInLowerThird = mouseY > (containerHeight * 2 / 3);

        // Position tooltip relative to container
        this.tooltip.x = mouseX + 10;
        this.tooltip.above = isInLowerThird;

        if (isInLowerThird) {
            // Position above mouse
            this.tooltip.y = mouseY;
        } else {
            // Position below mouse
            this.tooltip.y = mouseY + 20;
        }
    }

    showTooltipForPoint(event: MouseEvent, point: DataPoint): void {
        this.hoveredLineKeys.clear();
        this.hoveredLineKeys.add(point.key);

        // Store the URLs that should remain opaque (only those in this point)
        this.hoveredUrls.clear();
        point.logEntries.forEach(entry => {
            this.hoveredUrls.add(`${entry.url}|${entry.responseCode}`);
        });

        this.tooltip.visible = true;
        this.updateTooltipPosition(event);

        // Build tooltip text from log entries
        const entries = point.logEntries.map(entry =>
            `URL: ${entry.url} (${entry.responseCode}) - Count: ${entry.count}`
        );
        this.tooltip.text = `Time: ${point.timeRange}\nCount: ${point.count}\n${entries.join('\n')}`;
    }

    showTooltipForSegment(event: MouseEvent, segment: LineSegment): void {
        this.hoveredLineKeys.clear();

        // Find all URLs in this specific segment (only these should remain opaque)
        const urlsInHoveredSegment = new Set<string>();
        segment.urls.forEach(u => {
            urlsInHoveredSegment.add(`${u.url}|${u.responseCode}`);
        });

        // Find all segments that contain at least one of the URLs from the hovered segment
        // This will show all segments for those URLs, but we'll filter labels separately
        this.lineSegments.forEach(s => {
            const hasMatchingUrl = s.urls.some(u => urlsInHoveredSegment.has(`${u.url}|${u.responseCode}`));
            if (hasMatchingUrl) {
                this.hoveredLineKeys.add(s.key);
            }
        });

        // Also highlight the points that are part of these segments
        this.lineSegments.forEach(s => {
            if (this.hoveredLineKeys.has(s.key)) {
                this.hoveredLineKeys.add(s.startPoint.key);
                this.hoveredLineKeys.add(s.endPoint.key);
            }
        });

        // Store the URLs that should remain opaque (only those in the hovered segment)
        this.hoveredUrls = urlsInHoveredSegment;

        this.tooltip.visible = true;
        this.updateTooltipPosition(event);

        // Build tooltip text listing all URLs in this segment
        const urlList = segment.urls.map(u => `URL: ${u.url} (${u.responseCode})`).join('\n');
        this.tooltip.text = `From: ${segment.startPoint.timeRange} (Count: ${segment.startPoint.count})\n` +
                            `To: ${segment.endPoint.timeRange} (Count: ${segment.endPoint.count})\n${urlList}`;
    }

    hideTooltip(): void {
        this.hoveredLineKeys.clear(); // Clear hovered lines when mouse leaves
        this.hoveredUrls.clear(); // Clear hovered URLs
        this.tooltip.visible = false;
    }
}
